/**
 * What is sealed inside a person's `pf`: their name and their character.
 *
 * Its own module because BOTH surfaces read it -- a parent's device through
 * family/state.ts and a child's through child/state.ts -- and the two are
 * kept from sharing a client, storage or state on purpose (see main.tsx).
 * This depends on nothing but the avatar's own rules, so it can sit between
 * them without joining them.
 */

import { normalise, type AvatarSpec } from './avatar';

export interface PersonFragment {
  name: string;
  avatar?: AvatarSpec | null;
}

/**
 * A decrypted fragment, read defensively.
 *
 * Fragments are written by parents' devices, by children's devices and, for a
 * family that came over from the wall display, by that too -- so anything in
 * here may be old, partial or odd. A character that fails to parse becomes no
 * character, never a crash; `normalise` makes whatever is there drawable.
 */
export function parseFragment(json: string): PersonFragment | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as { name?: unknown; avatar?: unknown };
    const avatar =
      parsed.avatar && typeof parsed.avatar === 'object'
        ? normalise(parsed.avatar as Partial<AvatarSpec>)
        : null;
    return { name: typeof parsed.name === 'string' ? parsed.name : '', avatar };
  } catch {
    return null;
  }
}

/** The JSON that goes into `pf`. No `avatar` key at all when there is none. */
export function sealable(fragment: PersonFragment): string {
  return JSON.stringify(
    fragment.avatar ? { name: fragment.name, avatar: fragment.avatar } : { name: fragment.name },
  );
}
